import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { AccessGroup } from '@/shared/access';
import { useEmployees } from '@/features/employees';
import { LoadingState } from '@/shared/ui';

import { useAccessGroup, useSetAccessGroupMembers } from '../../hooks';

interface GroupMembersDialogProps {
  /** The group being edited; `null` closes the dialog. */
  group: AccessGroup | null;
  onClose: () => void;
}

/**
 * Bulk membership editing.
 *
 * <p>Membership is submitted as the complete list, not as add/remove deltas: two administrators editing the
 * same group concurrently then produce a last-write-wins outcome that is at least comprehensible, rather
 * than a merge of two partial intents that matches neither person's mental model.
 */
export function GroupMembersDialog({ group, onClose }: GroupMembersDialogProps) {
  const { t } = useTranslation('access');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // The list endpoint omits `memberIds` on purpose, so the dialog re-reads the single group to learn who
  // is actually in it rather than inferring membership from the count.
  const detail = useAccessGroup(group?.id);
  const employees = useEmployees({ search: search.trim() || undefined, size: 100 });
  const save = useSetAccessGroupMembers();

  const memberIds = useMemo(() => detail.data?.memberIds ?? [], [detail.data]);

  useEffect(() => {
    if (group) setSelected(new Set(memberIds));
  }, [group, memberIds]);

  if (!group) return null;

  const items = employees.data?.items ?? [];

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('members.title', { name: group.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.isError && <Alert severity="error">{save.error?.message}</Alert>}
          <TextField
            size="small"
            label={t('members.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            {t('members.selectedCount', { count: selected.size })}
          </Typography>
          {employees.isLoading ? (
            <LoadingState label={t('members.loading')} />
          ) : (
            <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {items.map((employee) => (
                <ListItemButton key={employee.id} onClick={() => toggle(employee.id)} dense>
                  <Checkbox
                    edge="start"
                    checked={selected.has(employee.id)}
                    tabIndex={-1}
                    disableRipple
                    slotProps={{ input: { 'aria-label': employee.email } }}
                  />
                  <ListItemText primary={employee.name ?? employee.email} secondary={employee.email} />
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { groupId: group.id, userIds: [...selected] },
              { onSuccess: onClose },
            )
          }
        >
          {t('members.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
